import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Mail, Download, Send, Loader2, Plus, X, CheckCircle2,
} from "lucide-react";
import {
  execDigestSettings, execDigestSettingsSave, execDigestSend,
  execDigestDownloadPdf,
} from "@/lib/api";
import { toast } from "sonner";

/**
 * WeeklyDigestCard — plain-English control for the Executive Dashboard
 * weekly PDF digest. Everything a customer needs in one card:
 *  - one toggle to turn it on/off
 *  - one dropdown for day + time
 *  - a short list of extra people to send it to
 *  - buttons to download the PDF now or send a test email
 *
 * The copy is intentionally jargon-free so any business owner can
 * understand and manage it themselves.
 */

const DAYS = [
  { v: 0, label: "Monday" },
  { v: 1, label: "Tuesday" },
  { v: 2, label: "Wednesday" },
  { v: 3, label: "Thursday" },
  { v: 4, label: "Friday" },
  { v: 5, label: "Saturday" },
  { v: 6, label: "Sunday" },
];

const HOURS = Array.from({ length: 24 }, (_, i) => ({
  v: i,
  label: `${String(i).padStart(2, "0")}:00`,
}));

// Common timezones — the API accepts any IANA name, this list is just for
// non-technical customers.
const COMMON_TZS = [
  "UTC",
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "America/Toronto",
  "America/Mexico_City",
  "America/Sao_Paulo",
  "Europe/London",
  "Europe/Berlin",
  "Europe/Madrid",
  "Europe/Athens",
  "Africa/Johannesburg",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
  "Pacific/Auckland",
];

export default function WeeklyDigestCard() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [newRecipient, setNewRecipient] = useState({ name: "", email: "" });

  useEffect(() => {
    execDigestSettings()
      .then(setS)
      .catch(() => setS(null));
  }, []);

  const persist = async (patch) => {
    setSaving(true);
    try {
      const next = await execDigestSettingsSave(patch);
      setS(next);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const addRecipient = async () => {
    const email = (newRecipient.email || "").trim().toLowerCase();
    if (!email || !email.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    const list = [...(s.extra_recipients || []), {
      email, name: newRecipient.name.trim(),
    }];
    if (list.length > 10) {
      toast.error("You can add up to 10 extra people");
      return;
    }
    await persist({ extra_recipients: list });
    setNewRecipient({ name: "", email: "" });
  };

  const removeRecipient = async (email) => {
    const list = (s.extra_recipients || []).filter((r) => r.email !== email);
    await persist({ extra_recipients: list });
  };

  const sendTest = async () => {
    setSending(true);
    try {
      const r = await execDigestSend(false);
      toast.success(
        `Sent to ${r.sent.filter((x) => x.id !== "dryrun").length} recipient(s)`,
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send");
    } finally {
      setSending(false);
    }
  };

  const download = async () => {
    setDownloading(true);
    try {
      await execDigestDownloadPdf();
      toast.success("PDF downloaded");
    } catch {
      toast.error("Could not build PDF");
    } finally {
      setDownloading(false);
    }
  };

  if (!s) {
    return (
      <Card className="mt-6 border border-slate-200 bg-white p-5"
             data-testid="digest-card-loading">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading weekly digest…
        </div>
      </Card>
    );
  }

  const dayLabel = DAYS.find((d) => d.v === s.send_day)?.label || "Monday";
  const hourLabel = HOURS.find((h) => h.v === s.send_hour)?.label || "08:00";

  return (
    <Card
      className="mt-6 border border-slate-200 bg-white p-5"
      data-testid="digest-card"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500">
            <Mail className="h-4 w-4" /> Weekly digest
          </div>
          <h3 className="mt-1 font-heading text-lg font-bold text-slate-900">
            A PDF summary in your inbox — no login needed
          </h3>
          <p className="mt-1 text-sm text-slate-600">
            Every week we email you a one-page PDF with the same six numbers
            you see above. Turn it on, pick a time, and add anyone else who
            should see it (partner, accountant, agency).
          </p>
        </div>
        <Badge
          variant="outline"
          className={
            s.enabled
              ? "border-emerald-300 bg-emerald-50 text-emerald-800"
              : "border-slate-300 bg-slate-50 text-slate-600"
          }
          data-testid="digest-status-badge"
        >
          {s.enabled ? (
            <>
              <CheckCircle2 className="mr-1 h-3 w-3" /> On
            </>
          ) : (
            "Off"
          )}
        </Badge>
      </div>

      {/* Toggle + schedule */}
      <div className="mt-4 grid gap-3 md:grid-cols-4">
        <div>
          <label className="text-xs font-semibold text-slate-500">Status</label>
          <div className="mt-1 flex gap-2">
            <Button
              size="sm"
              variant={s.enabled ? "default" : "outline"}
              className={s.enabled ? "bg-emerald-600 text-white hover:bg-emerald-700" : ""}
              onClick={() => persist({ enabled: true })}
              disabled={saving}
              data-testid="digest-enable-btn"
            >
              On
            </Button>
            <Button
              size="sm"
              variant={!s.enabled ? "default" : "outline"}
              className={!s.enabled ? "bg-slate-800 text-white hover:bg-slate-900" : ""}
              onClick={() => persist({ enabled: false })}
              disabled={saving}
              data-testid="digest-disable-btn"
            >
              Off
            </Button>
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500">Send on</label>
          <select
            value={s.send_day}
            onChange={(e) => persist({ send_day: Number(e.target.value) })}
            disabled={saving || !s.enabled}
            className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm disabled:opacity-60"
            data-testid="digest-day-select"
          >
            {DAYS.map((d) => (
              <option key={d.v} value={d.v}>{d.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500">At (local time)</label>
          <select
            value={s.send_hour}
            onChange={(e) => persist({ send_hour: Number(e.target.value) })}
            disabled={saving || !s.enabled}
            className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm disabled:opacity-60"
            data-testid="digest-hour-select"
          >
            {HOURS.map((h) => (
              <option key={h.v} value={h.v}>{h.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500">Timezone</label>
          <select
            value={s.tz}
            onChange={(e) => persist({ tz: e.target.value })}
            disabled={saving || !s.enabled}
            className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm disabled:opacity-60"
            data-testid="digest-tz-select"
          >
            {(COMMON_TZS.includes(s.tz) ? COMMON_TZS : [s.tz, ...COMMON_TZS]).map((tz) => (
              <option key={tz} value={tz}>{tz}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-3 text-xs text-slate-500" data-testid="digest-next-line">
        {s.enabled
          ? `Next email: every ${dayLabel} at ${hourLabel} (${s.tz}). Sent to you${
              (s.extra_recipients || []).length
                ? ` + ${s.extra_recipients.length} other`
                : ""
            }${(s.extra_recipients || []).length === 1 ? "" : (s.extra_recipients || []).length ? "s" : ""}.`
          : "Weekly digest is off. Turn it on and we'll send the PDF automatically."}
      </div>

      {/* Extra recipients */}
      <div className="mt-5 border-t border-slate-100 pt-4">
        <div className="text-xs font-semibold text-slate-500">Send it to</div>
        <ul className="mt-2 space-y-1 text-sm" data-testid="digest-recipient-list">
          <li className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-1.5">
            <span>You (account owner)</span>
            <span className="text-xs text-slate-400">always included</span>
          </li>
          {(s.extra_recipients || []).map((r) => (
            <li
              key={r.email}
              className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-1.5"
              data-testid={`digest-recipient-${r.email}`}
            >
              <span>
                <b>{r.name || r.email}</b>
                {r.name ? <span className="ml-2 text-slate-500">{r.email}</span> : null}
              </span>
              <button
                onClick={() => removeRecipient(r.email)}
                className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                aria-label={`Remove ${r.email}`}
                data-testid={`digest-recipient-remove-${r.email}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
        {(s.extra_recipients || []).length < 10 && (
          <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
            <input
              type="text"
              placeholder="Name (optional)"
              value={newRecipient.name}
              onChange={(e) =>
                setNewRecipient({ ...newRecipient, name: e.target.value })
              }
              className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
              data-testid="digest-new-name"
            />
            <input
              type="email"
              placeholder="their@email.com"
              value={newRecipient.email}
              onChange={(e) =>
                setNewRecipient({ ...newRecipient, email: e.target.value })
              }
              className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
              data-testid="digest-new-email"
            />
            <Button
              size="sm"
              variant="outline"
              onClick={addRecipient}
              disabled={saving}
              data-testid="digest-recipient-add"
            >
              <Plus className="mr-1 h-3.5 w-3.5" /> Add
            </Button>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="mt-5 flex flex-wrap gap-2 border-t border-slate-100 pt-4">
        <Button
          variant="outline"
          onClick={download}
          disabled={downloading}
          data-testid="digest-download-btn"
        >
          {downloading ? (
            <Loader2 className="mr-1 h-4 w-4 animate-spin" />
          ) : (
            <Download className="mr-1 h-4 w-4" />
          )}
          Download PDF now
        </Button>
        <Button
          className="bg-orange-600 text-white hover:bg-orange-700"
          onClick={sendTest}
          disabled={sending}
          data-testid="digest-send-btn"
        >
          {sending ? (
            <Loader2 className="mr-1 h-4 w-4 animate-spin" />
          ) : (
            <Send className="mr-1 h-4 w-4" />
          )}
          Send it now
        </Button>
      </div>
    </Card>
  );
}
